import React, { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { authApi } from '../api/services';
import BrandMark from '../components/BrandMark';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';

// Grain texture — SVG feTurbulence fractalNoise overlay encoded as data-URI.
const grainSvg =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'>` +
      `<filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/></filter>` +
      `<rect width='100%' height='100%' filter='url(#n)' opacity='1'/>` +
      `</svg>`,
  );

// Field + button tokens — aligned with LoginPage so the auth funnel is one piece.
const FIELD_CLASS =
  'h-12 rounded-[11px] border border-[rgba(234,238,246,0.12)] bg-[rgba(234,238,246,0.04)] px-[15px] py-[13px] text-[15px] text-[#EAEEF6] placeholder:text-[rgba(234,238,246,0.30)] focus-visible:border-[#7DB1FF] focus-visible:ring-[3px] focus-visible:ring-[rgba(125,177,255,0.16)]';

const PRIMARY_BUTTON =
  'h-12 w-full rounded-[11px] border-0 bg-[#EAEEF6] text-[15px] font-semibold text-[#070A12] transition-transform duration-150 hover:-translate-y-px hover:bg-[#EAEEF6] hover:shadow-[0_10px_32px_rgba(125,177,255,0.22)] disabled:opacity-60 disabled:hover:translate-y-0';

function getPasswordStrength(pw: string): { score: number; label: string; color: string } {
  if (!pw) return { score: 0, label: '', color: 'transparent' };
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[a-z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  // Colour palette aligned with the cross-surface RAG split — focus-blue is
  // reserved for the focus role, so strong sits on emerald and warn states use
  // amber/rose against the dark cinematic backdrop.
  if (score <= 2) return { score: 25, label: 'Weak', color: '#F87171' };
  if (score <= 3) return { score: 50, label: 'Fair', color: '#FBBF24' };
  if (score <= 4) return { score: 75, label: 'Good', color: '#FBBF24' };
  return { score: 100, label: 'Strong', color: '#6EE7B7' };
}

// ---------------------------------------------------------------------------
// PasswordResetPage — themed to match LoginPage. Reads email + token from
// the URL, shows a strength meter while typing, and a matched/mismatched
// hint under confirm. Mirrors LoginPage's backdrop, .rise stagger, and
// leave-veil so the auth funnel reads as one experience.
// ---------------------------------------------------------------------------

export default function PasswordResetPage() {
  // v2.2.3 — useSearchParams instead of `window.location.search`. Same
  // architectural fix the AppRouter blank-login bug needed: React-Router
  // soft nav updates its own location context but doesn't re-read
  // window.location, so any stale render after nav would carry old params.
  const [searchParams] = useSearchParams();
  const emailParam = searchParams.get('email') ?? '';
  const tokenParam = searchParams.get('token') ?? '';
  const navigate = useNavigate();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  // Document title parity with Login.
  useEffect(() => {
    const prev = document.title;
    document.title = 'Choose a new password · Cadence';
    return () => { document.title = prev; };
  }, []);

  // Entrance — triple-redundant trigger.
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const reveal = () => { if (!cancelled) setReady(true); };
    const t1 = window.setTimeout(reveal, 120);
    const t2 = window.setTimeout(reveal, 200);
    window.addEventListener('load', reveal);
    const onVis = () => { if (document.visibilityState === 'visible') reveal(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.removeEventListener('load', reveal);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  // Leave-veil — soft fade up on successful submit before swapping to the
  // success state, and again when the user clicks through to Sign In.
  const [leaving, setLeaving] = useState(false);

  const strength = useMemo(() => getPasswordStrength(newPassword), [newPassword]);
  const passwordsMatch = confirmPassword.length > 0 && newPassword === confirmPassword;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await authApi.resetPassword({
        email: emailParam,
        token: tokenParam,
        newPassword,
      });
      setLeaving(true);
      await new Promise((r) => setTimeout(r, 250));
      setSuccess(true);
      setLeaving(false);
    } catch (err: any) {
      const errors = err.response?.data?.errors;
      if (Array.isArray(errors)) {
        setError(errors.join(' '));
      } else {
        setError(err.response?.data?.error || 'Password reset failed. The link may have expired.');
      }
      setLeaving(false);
    } finally {
      setLoading(false);
    }
  };

  const goToSignIn = async () => {
    setLeaving(true);
    await new Promise((r) => setTimeout(r, 250));
    navigate('/login');
  };

  return (
    <div className="dark relative flex min-h-screen items-center justify-center overflow-hidden bg-[#070A12]">
      {/* Ambient backdrop layer (z=0). */}
      <div aria-hidden className="pointer-events-none absolute inset-0 z-0">
        <div
          className="absolute left-1/2 bottom-[-24%] h-[78vh] w-[120vw] -translate-x-1/2 rounded-full blur-[14px]"
          style={{
            background:
              'radial-gradient(ellipse at center, rgba(255,176,122,0.14) 0%, transparent 62%)',
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(120% 90% at 50% -10%, rgba(125,177,255,0.05), transparent 55%), linear-gradient(180deg, rgba(0,0,0,0.28), transparent 32%, transparent 68%, rgba(0,0,0,0.45))',
          }}
        />
      </div>

      {/* Grain (z=1). */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-[1]"
        style={{
          backgroundImage: `url("${grainSvg}")`,
          backgroundSize: '160px 160px',
          opacity: 0.04,
          mixBlendMode: 'overlay',
        }}
      />

      {/* Leave-veil (z=60). */}
      <div
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-0 z-[60] transition-opacity duration-[520ms] ease-out',
          leaving ? 'opacity-100' : 'opacity-0',
        )}
        style={{ background: '#070A12' }}
      />

      {/* Form column (z=2). */}
      <div className="relative z-[2] mx-4 flex w-full max-w-[392px] flex-col p-[32px]">
        <div
          className={cn('rise mb-5', ready && 'is-revealed')}
          style={{ '--d': '120ms' } as React.CSSProperties}
        >
          <BrandMark size="md" showStratiumLine inlineStratiumTagline accent="light" />
        </div>

        <h1
          className={cn(
            'rise mb-[10px] font-serif text-[33px] font-light leading-[1.12] tracking-[-0.01em] text-white',
            ready && 'is-revealed',
          )}
          style={{ '--d': '260ms' } as React.CSSProperties}
        >
          Choose a new password.
        </h1>
        <p
          className={cn(
            'rise mb-[30px] text-[15px] font-light tracking-wide text-[rgba(234,238,246,0.62)]',
            ready && 'is-revealed',
          )}
          style={{ '--d': '380ms' } as React.CSSProperties}
        >
          {emailParam ? (
            <>Setting password for <span className="text-[rgba(234,238,246,0.85)]">{emailParam}</span>.</>
          ) : (
            <>Pick something strong you can remember.</>
          )}
        </p>

        {error && (
          <Alert variant="error" className="mb-3 rounded-xl">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {success ? (
          <div
            className={cn('rise flex flex-col gap-[14px]', ready && 'is-revealed')}
            style={{ '--d': '520ms' } as React.CSSProperties}
          >
            <Alert variant="success" className="rounded-xl">
              <AlertDescription>
                Password reset successfully. You can now sign in with your new password.
              </AlertDescription>
            </Alert>
            <Button
              onClick={goToSignIn}
              className={cn('mt-[6px] h-12 w-full text-[15px] font-semibold', PRIMARY_BUTTON)}
            >
              Go to sign in
            </Button>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className={cn('rise flex flex-col gap-[14px]', ready && 'is-revealed')}
            style={{ '--d': '520ms' } as React.CSSProperties}
          >
            <div className="flex flex-col gap-[7px]">
              <Label htmlFor="new-password" className="text-[12.5px] tracking-[0.02em] text-[rgba(234,238,246,0.40)]">
                New password
              </Label>
              <Input
                id="new-password"
                type="password"
                required
                autoFocus
                minLength={8}
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className={FIELD_CLASS}
              />
              {newPassword && (
                <div className="mt-1 flex items-center gap-2">
                  <div className="h-1 flex-1 overflow-hidden rounded-full bg-[rgba(234,238,246,0.08)]">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{ width: `${strength.score}%`, backgroundColor: strength.color }}
                    />
                  </div>
                  <p
                    className="min-w-[40px] text-[11px] tracking-wide"
                    style={{ color: strength.color }}
                  >
                    {strength.label}
                  </p>
                </div>
              )}
              <p className="text-[11px] text-[rgba(234,238,246,0.40)]">
                Min 8 chars, uppercase, lowercase, number, special character.
              </p>
            </div>

            <div className="flex flex-col gap-[7px]">
              <Label htmlFor="confirm-password" className="text-[12.5px] tracking-[0.02em] text-[rgba(234,238,246,0.40)]">
                Confirm new password
              </Label>
              <Input
                id="confirm-password"
                type="password"
                required
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={FIELD_CLASS}
              />
              {confirmPassword && (
                <p
                  className="text-[11px] tracking-wide"
                  style={{ color: passwordsMatch ? '#6EE7B7' : '#F87171' }}
                >
                  {passwordsMatch ? 'Passwords match' : 'Passwords do not match'}
                </p>
              )}
            </div>

            <Button
              type="submit"
              disabled={loading || !newPassword || !confirmPassword}
              className={cn('mt-[6px] h-12 w-full text-[15px] font-semibold', PRIMARY_BUTTON)}
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Reset password'}
            </Button>
          </form>
        )}

        <div
          className={cn(
            'rise mt-6 flex items-center justify-center gap-3 text-[12px] text-[rgba(234,238,246,0.62)]',
            ready && 'is-revealed',
          )}
          style={{ '--d': '1000ms' } as React.CSSProperties}
        >
          <Link to="/login" className="hover:text-white">
            Back to sign in
          </Link>
          <span aria-hidden className="text-[rgba(234,238,246,0.30)]">·</span>
          <Link to="/signup" className="hover:text-white">
            14 day free trial
          </Link>
        </div>

        <p
          className={cn(
            'rise mt-8 text-center text-[11px] tracking-wide text-[rgba(234,238,246,0.30)]',
            ready && 'is-revealed',
          )}
          style={{ '--d': '1200ms' } as React.CSSProperties}
        >
          Cadence is the product. Stratium is the company behind it.
        </p>
      </div>
    </div>
  );
}
